/* eslint-disable function-paren-newline */
// @flow
import React from 'react';
import styled from 'styled-components';

import { Grid } from 'layout-core-components';
import { Heading } from 'primitive-components';

import EditableText from 'editable-text';
import AddCategorySegmentButton from './AddCategorySegmentButton';
import type { Segment, SegmentCategory, NewSegment } from './types';

type SegmentListFormProps = {
  segmentCategory: SegmentCategory,
  onSegmentAdded: NewSegment => Promise<void>,
  onSegmentUpdated: Segment => Promise<void>,
  onSegmentDeleted: Segment => Promise<void>,
};

const StyledHeading = styled(Heading)`
  margin-bottom: 8px;
`;

const ScrollContainer = styled.div`
  max-height: calc(100vh - 375px);
  overflow: scroll;
`;

export default function SegmentListForm({
  segmentCategory: { segments, name },
  onSegmentAdded,
  onSegmentDeleted,
  onSegmentUpdated,
}: SegmentListFormProps) {
  return (
    <Grid column="auto" rowSpacing="small">
      <StyledHeading size={3}>{name}</StyledHeading>
      <AddCategorySegmentButton
        startOpen={segments.length === 0}
        segmentCategoryName={name}
        onSegmentCategoryAdded={async segment => {
          try {
            await onSegmentAdded(segment);
          } catch (err) {
            if (err && err.message === 'GraphQL error: 400 Bad Request') {
              /* eslint-disable-next-line no-alert */
              window.alert(`${segment.name} already exists.`);
            } else {
              throw err;
            }
          }
        }}
      />
      <ScrollContainer>
        {segments
          .slice()
          .sort((a, b) => Number(a.id) - Number(b.id))
          .map((segment: Segment) => {
            const updateSegmentName = async segmentName => {
              const updatedSegment = {
                ...segment,
                name: segmentName,
              };

              try {
                await onSegmentUpdated(updatedSegment);
                return true;
              } catch (err) {
                if (err && err.message === 'GraphQL error: 400 Bad Request') {
                  /* eslint-disable-next-line no-alert */
                  window.alert(`${segmentName} already exists.`);
                  return false;
                }
                throw err;
              }
            };

            const deleteSegment = () => {
              onSegmentDeleted(segment).catch(err => {
                if (err && err.message === 'GraphQL error: 400 Bad Request') {
                  // eslint-disable-next-line no-alert
                  window.alert(
                    `Looks like there are transactions associated with this tag. To remove it, contact your bookkeeping team.`,
                  );
                } else {
                  throw err;
                }
              });
            };

            return (
              <EditableText
                key={segment.id}
                maxLength={30}
                value={segment.name}
                onChangeSubmitted={updateSegmentName}
                onDeleteRequested={deleteSegment}
              />
            );
          })}
      </ScrollContainer>
    </Grid>
  );
}
